/* global console, window */

import { AppointmentInfo } from "../taskpane/domain/AppointmentInfo";
import { FilterOptions } from "../taskpane/domain/FilterOptions";
import { FilterQueryParameters } from "../taskpane/domain/FilterQueryParameters";
import { RecurrenceInfo } from "../taskpane/domain/RecurrenceInfo";
import { Room } from "../taskpane/domain/Room";
import { RoomQueryParameters } from "../taskpane/domain/RoomQueryParameters";
import { getAvailableRooms, getFilterOptions, getRoomDetails } from "./ArchibusApi";
import {
  getAppointmentEnd,
  getAppointmentStart,
  getAttendeeEmails,
  getLocationEmails,
  getRecurrenceInfo,
  setLocation,
} from "./OfficeApi";

const findAvailableRoomsForItem = (
  buildingId: string,
  floorId: string,
  arrangeTypeId: string,
  capacity: number | null,
  attributes: string[],
  setRooms: (rooms: Room[]) => void,
  appointmentInfo: AppointmentInfo,
  checkProceed: () => boolean
) => {
  return getAvailableRooms(
    new RoomQueryParameters({
      buildingId: buildingId,
      floorId: floorId,
      arrangeTypeId: arrangeTypeId,
      capacity: capacity,
      attributes: attributes,
      appointmentInfo: appointmentInfo,
    })
  ).then((data: Room[]) => {
    if (checkProceed()) {
      setRooms(data);
    }
  });
};

export const findAvailableRooms = async (
  buildingId: string,
  floorId: string,
  arrangeTypeId: string,
  capacity: number | null,
  attributes: string[],
  setRooms: (rooms: Room[]) => void,
  // if caller didn't pass a check function, always proceed to the next step
  checkProceed = () => true
) => {
  if (buildingId == "") {
    console.log("No building selected. Skip looking for available rooms.");
  } else {
    return Promise.all([getAppointmentStart(), getAppointmentEnd(), getAttendeeEmails(), getRecurrenceInfo()]).then(
      (values: [Date, Date, string[], RecurrenceInfo | null]) => {
        if (checkProceed()) {
          const appointmentInfo = new AppointmentInfo(values);
          return findAvailableRoomsForItem(
            buildingId,
            floorId,
            arrangeTypeId,
            capacity,
            attributes,
            setRooms,
            appointmentInfo,
            checkProceed
          );
        } else {
          return;
        }
      }
    );
  }
};

export const showRoomInfo = async (room: Room) => {
  console.log(`Show room info via ${room.info}`);
  window.open(room.info);
};

async function filterArchibusRoomEmails(
  emails: string[],
  archibusRoomCache: Map<string, Room>,
  otherEmailCache: Set<string>
): Promise<string[]> {
  // Only call Archibus REST API for emails not found in cache.
  // 1. Find unknown emails to check with Archibus API.
  const unknownEmails = emails.filter((email) => !archibusRoomCache.has(email) && !otherEmailCache.has(email));
  if (unknownEmails.length > 0) {
    // 2. Call the Archibus API (and wait for it to complete).
    const rooms = await getRoomDetails(emails);
    // 3. Add all returned rooms to the Archibus rooms cache
    rooms.forEach((room) => archibusRoomCache.set(room.mailbox_id, room));
    // 4. Add other emails in separate cache.
    unknownEmails.forEach((email: string) => {
      if (!archibusRoomCache.has(email)) {
        otherEmailCache.add(email);
      }
    });
  }
  // 5. Return the input emails that exist in the room cache.
  return emails.filter((email) => archibusRoomCache.has(email));
}

export const selectRoom = async (room: Room, archibusRoomCache: Map<string, Room>, otherEmailCache: Set<string>) => {
  console.log(`Select room ${room.mailbox_id}`);
  archibusRoomCache.set(room.mailbox_id, room);
  // pass function to filter emails
  setLocation(room, async (emails) => {
    return filterArchibusRoomEmails(emails, archibusRoomCache, otherEmailCache);
  });
};

export const getInitialFilterOptions = async (): Promise<FilterOptions> => {
  // Retrieve location emails from OfficeApi, then pass them in initializer of FilterQueryParameters.
  // If retrieving the emails fails, proceed without them.
  let emails: string[] = [];
  try {
    emails = await getLocationEmails();
  } catch (e) {
    console.log(`Get location emails failed - ${e}`);
  }
  return getFilterOptions(new FilterQueryParameters({ emails: emails }));
};
